import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Database,
  HeartHandshake,
  LayoutDashboard,
  LockKeyhole,
  LogIn,
  MessageSquareText,
  Plus,
  Save,
  Search,
  Settings2,
  UsersRound,
} from "lucide-react";
import { ADMIN_AUTH_REQUIRED_EVENT, ApiError, api, json } from "../api";
import { AdminMediaLibrary } from "../components/AdminMediaLibrary";

type AdminWoman = {
  user_id: string;
  name: string;
  slug: string;
  age: number;
  country_code: string;
  city_code: string;
  country_name_zh: string;
  city_name_zh: string;
  city_zh: string;
  city_en: string;
  district_zh: string;
  district_en: string;
  profession_zh: string;
  profession_en: string;
  bio_zh: string;
  bio_en: string;
  distance_m: number;
  distance_mode: "dynamic" | "fixed";
  active: boolean;
  online: boolean;
  verified: boolean;
  sort_order: number;
  interests: string[];
  contacts: Array<{ platform: string; handle: string }>;
  media: Array<{ id: string; kind: string; url: string }>;
  archived_at: string | null;
};
type Member = {
  id: string;
  name: string;
  email: string | null;
  account_status: string;
  is_vip: boolean;
  vip_expires_at: string | null;
  created_at: string;
  city_zh: string | null;
  country_zh: string | null;
  observed_at: string | null;
};
const blank = {
  name: "",
  slug: "",
  age: 25,
  countryCode: "",
  cityCode: "",
  professionZh: "",
  professionEn: "",
  bioZh: "",
  bioEn: "",
  distanceM: 500,
  distanceMode: "dynamic" as "dynamic" | "fixed",
  active: true,
  online: true,
  verified: true,
  sortOrder: 0,
  interests: "",
  whatsapp: "",
  telegram: "",
  instagram: "",
  futurePassword: "",
};

function useDebouncedValue(value: string, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
}

export function AdminPage() {
  const queryClient = useQueryClient();
  const [authExpired, setAuthExpired] = useState(false);
  const overview = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () =>
      api<{
        women: number;
        men: number;
        conversations: number;
        pendingJobs: number;
      }>("/admin/overview"),
    retry: false,
  });
  useEffect(() => {
    const requireLogin = () => setAuthExpired(true);
    window.addEventListener(ADMIN_AUTH_REQUIRED_EVENT, requireLogin);
    return () => window.removeEventListener(ADMIN_AUTH_REQUIRED_EVENT, requireLogin);
  }, []);
  const loginSucceeded = async () => {
    const result = await overview.refetch();
    if (result.error) return;
    await queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("admin-") &&
        query.queryKey[0] !== "admin-overview",
    });
    setAuthExpired(false);
  };
  if (overview.isLoading)
    return (
      <div className="admin-loader">
        <span />
      </div>
    );
  if (
    authExpired ||
    (overview.error instanceof ApiError && overview.error.status === 401)
  )
    return <AdminLogin onSuccess={loginSucceeded} />;
  return <AdminDashboard overview={overview.data!} />;
}

function AdminLogin({ onSuccess }: { onSuccess: () => void | Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useMutation({
    mutationFn: () => api("/admin/login", json("POST", { email, password })),
    onSuccess: () => void onSuccess(),
  });
  return (
    <main className="admin-login">
      <section>
        <div className="admin-brand">
          <HeartHandshake />
          <span>
            <b>Nearby Heart</b>
            <small>Operations Console · v1.0</small>
          </span>
        </div>
        <h1>管理员登录</h1>
        <p>女性资料、招呼序列、会员与任务状态统一管理。</p>
        <label>
          管理员邮箱
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
          />
        </label>
        <label>
          密码
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
        </label>
        {login.isError && <p className="admin-error">账号或密码错误</p>}
        <button onClick={() => login.mutate()} disabled={!email || !password}>
          <LogIn />
          进入管理后台
        </button>
        <small>
          <LockKeyhole /> 管理后台受 HTTPS 与管理员账号保护
        </small>
      </section>
    </main>
  );
}

function AdminDashboard({
  overview,
}: {
  overview: {
    women: number;
    men: number;
    conversations: number;
    pendingJobs: number;
  };
}) {
  const [tab, setTab] = useState("overview");
  const items = [
    { id: "overview", label: "概览", icon: LayoutDashboard },
    { id: "women", label: "女性资料", icon: UsersRound },
    { id: "members", label: "男会员", icon: Database },
    { id: "conversations", label: "会话只读", icon: MessageSquareText },
    { id: "jobs", label: "自动任务", icon: Activity },
  ];
  return (
    <main className="admin-app">
      <aside>
        <div className="admin-brand">
          <HeartHandshake />
          <span>
            <b>Nearby Heart</b>
            <small>管理后台 v1.0</small>
          </span>
        </div>
        <nav>
          {items.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={tab === id ? "active" : ""}
              onClick={() => setTab(id)}
            >
              <Icon />
              {label}
            </button>
          ))}
        </nav>
        <div className="admin-side-note">
          <LockKeyhole />
          <p>女性登录与人工聊天在1.0保持关闭。</p>
        </div>
      </aside>
      <section className="admin-main">
        <header>
          <div>
            <p>DEVELOPMENT CONSOLE</p>
            <h1>{items.find((item) => item.id === tab)?.label}</h1>
          </div>
          <span className="environment-pill">
            <i />
            受保护开发环境
          </span>
        </header>
        {tab === "overview" && <Overview data={overview} />}{" "}
        {tab === "women" && <WomenAdmin />}
        {tab === "members" && <MembersAdmin />}
        {tab === "conversations" && (
          <ReadOnlyTable
            endpoint="/admin/conversations"
            field="conversations"
          />
        )}
        {tab === "jobs" && (
          <ReadOnlyTable endpoint="/admin/jobs" field="jobs" />
        )}
      </section>
    </main>
  );
}

function Overview({
  data,
}: {
  data: {
    women: number;
    men: number;
    conversations: number;
    pendingJobs: number;
  };
}) {
  const cards = [
    ["女性资料", data.women, UsersRound],
    ["男会员", data.men, Database],
    ["会话", data.conversations, MessageSquareText],
    ["待处理任务", data.pendingJobs, Activity],
  ] as const;
  return (
    <div className="admin-overview">
      <div className="metric-grid">
        {cards.map(([label, value, Icon]) => (
          <article key={label}>
            <span>
              <Icon />
            </span>
            <div>
              <small>{label}</small>
              <b>{value}</b>
            </div>
          </article>
        ))}
      </div>
      <section className="admin-welcome">
        <div>
          <p>1.0 PRODUCT RULES</p>
          <h2>功能和权限都由服务端执行</h2>
          <ul>
            <li>临时用户长期保留，正式注册不丢失数据</li>
            <li>头像永久免费，相册前3张免费；视频第1个免费</li>
            <li>招呼模板激活时生成快照，同一组合只执行一次</li>
            <li>女性资料全部由管理员创建，登录功能保持关闭</li>
          </ul>
        </div>
        <div className="radar-graphic">
          <i />
          <i />
          <i />
          <HeartHandshake />
        </div>
      </section>
    </div>
  );
}

function WomenAdmin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const women = useQuery({
    queryKey: ["admin-women", debouncedSearch],
    queryFn: () => api<{ women: AdminWoman[]; total: number }>(`/admin/women?q=${encodeURIComponent(debouncedSearch)}`),
  });
  const [selected, setSelected] = useState<AdminWoman | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="admin-split">
      <section className="admin-table-card">
        <div className="table-toolbar">
          <div>
            <h2>女性资料</h2>
            <p>找到 {women.data?.total ?? 0} 位，包括已归档资料</p>
          </div>
          <button
            onClick={() => {
              setSelected(null);
              setCreating(true);
            }}
          >
            <Plus />
            新建女性
          </button>
        </div>
        <label className="admin-search">
          <Search />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索姓名、Slug、国家或城市" />
          {search && <button onClick={() => setSearch("")}>清除</button>}
        </label>
        <div className="women-table">
          {women.isError && <p className="admin-empty">搜索失败，请重试</p>}
          {!women.isLoading && !women.isError && women.data?.women.length === 0 && <p className="admin-empty">没有找到匹配的女性资料</p>}
          {women.data?.women.map((woman) => (
            <button
              key={woman.user_id}
              className={woman.archived_at ? "archived" : ""}
              onClick={() => {
                setSelected(woman);
                setCreating(false);
              }}
            >
              <img
                src={
                  woman.media.find((item) => item.kind === "avatar")?.url ??
                  "/dating-v1/seed/fallback.webp"
                }
                alt=""
              />
              <span>
                <b>
                  {woman.name}, {woman.age}
                </b>
                <small>
                  {woman.country_name_zh} · {woman.city_name_zh} · {woman.distance_m} m
                </small>
              </span>
              <i className={woman.online ? "online" : ""}>
                {woman.archived_at ? "已归档" : woman.online ? "在线" : "离线"}
              </i>
            </button>
          ))}
        </div>
      </section>
      {(selected || creating) && (
        <WomanEditor
          woman={selected}
          onClose={() => {
            setSelected(null);
            setCreating(false);
          }}
          onSaved={async () => {
            await queryClient.invalidateQueries({ queryKey: ["admin-women"] });
            setSelected(null);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function WomanEditor({
  woman,
  onClose,
  onSaved,
}: {
  woman: AdminWoman | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const initial = useMemo(
    () =>
      woman
        ? {
            name: woman.name,
            slug: woman.slug,
            age: woman.age,
            countryCode: woman.country_code,
            cityCode: woman.city_code,
            professionZh: woman.profession_zh,
            professionEn: woman.profession_en,
            bioZh: woman.bio_zh,
            bioEn: woman.bio_en,
            distanceM: woman.distance_m,
            distanceMode: woman.distance_mode,
            active: woman.active,
            online: woman.online,
            verified: woman.verified,
            sortOrder: woman.sort_order,
            interests: woman.interests.join(", "),
            whatsapp:
              woman.contacts.find((c) => c.platform === "whatsapp")?.handle ??
              "",
            telegram:
              woman.contacts.find((c) => c.platform === "telegram")?.handle ??
              "",
            instagram:
              woman.contacts.find((c) => c.platform === "instagram")?.handle ??
              "",
            futurePassword: "",
          }
        : blank,
    [woman],
  );
  const [form, setForm] = useState(initial);
  const locations = useQuery({
    queryKey: ["admin-locations"],
    queryFn: () => api<{ countries: Array<{ code: string; name_zh: string; cities: Array<{ code: string; name_zh: string }> }> }>("/admin/locations"),
    staleTime: 60 * 60 * 1000,
  });
  const availableCities = locations.data?.countries.find((country) => country.code === form.countryCode)?.cities ?? [];
  const payload = () => {
    const { futurePassword, ...profile } = form;
    return {
      ...profile,
      ...(futurePassword ? { futurePassword } : {}),
      interests: form.interests
        .split(/[,，]/)
        .map((item) => item.trim())
        .filter(Boolean),
      contacts: ["whatsapp", "telegram", "instagram"]
        .map((platform) => ({
          platform,
          handle: form[platform as keyof typeof form],
        }))
        .filter((item) => item.handle),
    };
  };
  const save = useMutation({
    mutationFn: () =>
      api(
        woman ? `/admin/women/${woman.user_id}` : "/admin/women",
        json(woman ? "PATCH" : "POST", payload()),
      ),
    onSuccess: onSaved,
  });
  const archive = useMutation({
    mutationFn: () =>
      api(`/admin/women/${woman!.user_id}`, { method: "DELETE" }),
    onSuccess: onSaved,
  });
  const set = (key: string, value: unknown) =>
    setForm((current) => ({ ...current, [key]: value }));
  return (
    <aside className="woman-editor">
      <header>
        <div>
          <small>{woman ? "编辑资料" : "创建资料"}</small>
          <h2>{form.name || "新女性"}</h2>
        </div>
        <button onClick={onClose}>×</button>
      </header>
      <div className="editor-scroll">
        <div className="form-grid">
          <Field
            label="姓名"
            value={form.name}
            onChange={(v) => set("name", v)}
          />
          <Field
            label="Slug"
            value={form.slug}
            onChange={(v) => set("slug", v)}
          />
          <Field
            label="年龄"
            type="number"
            value={form.age}
            onChange={(v) => set("age", Number(v))}
          />
          <Field
            label="距离（米）"
            type="number"
            value={form.distanceM}
            onChange={(v) => set("distanceM", Number(v))}
          />
          <label>
            距离模式
            <select value={form.distanceMode} onChange={(event) => set("distanceMode", event.target.value)}>
              <option value="dynamic">按男用户动态距离</option>
              <option value="fixed">固定使用资料距离</option>
            </select>
          </label>
          <label>
            国家（中文）
            <select value={form.countryCode} onChange={(event) => setForm((current) => ({ ...current, countryCode: event.target.value, cityCode: "" }))}>
              <option value="">请选择国家</option>
              {locations.data?.countries.map((country) => <option key={country.code} value={country.code}>{country.name_zh}</option>)}
            </select>
          </label>
          <label>
            城市（中文）
            <select value={form.cityCode} disabled={!form.countryCode} onChange={(event) => set("cityCode", event.target.value)}>
              <option value="">请选择城市</option>
              {availableCities.map((city) => <option key={city.code} value={city.code}>{city.name_zh}</option>)}
            </select>
          </label>
          <Field
            label="中文职业"
            value={form.professionZh}
            onChange={(v) => set("professionZh", v)}
          />
          <Field
            label="英文职业"
            value={form.professionEn}
            onChange={(v) => set("professionEn", v)}
          />
        </div>
        <label>
          中文简介
          <textarea
            value={form.bioZh}
            onChange={(e) => set("bioZh", e.target.value)}
          />
        </label>
        <label>
          英文简介
          <textarea
            value={form.bioEn}
            onChange={(e) => set("bioEn", e.target.value)}
          />
        </label>
        <Field
          label="兴趣（逗号分隔）"
          value={form.interests}
          onChange={(v) => set("interests", v)}
        />
        <Field
          label="未来女性登录密码（至少8位，1.0不启用登录）"
          type="password"
          value={form.futurePassword}
          onChange={(v) => set("futurePassword", v)}
        />
        <h3>联系方式</h3>
        <div className="form-grid">
          <Field
            label="WhatsApp"
            value={form.whatsapp}
            onChange={(v) => set("whatsapp", v)}
          />
          <Field
            label="Telegram"
            value={form.telegram}
            onChange={(v) => set("telegram", v)}
          />
          <Field
            label="Instagram"
            value={form.instagram}
            onChange={(v) => set("instagram", v)}
          />
        </div>
        <div className="toggle-row">
          <label>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set("active", e.target.checked)}
            />
            启用资料
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.online}
              onChange={(e) => set("online", e.target.checked)}
            />
            在线
          </label>
          <label>
            <input
              type="checkbox"
              checked={form.verified}
              onChange={(e) => set("verified", e.target.checked)}
            />
            已认证
          </label>
        </div>
        {woman && (
          <AdminMediaLibrary
            womanId={woman.user_id}
            onProfileMediaChange={() =>
              void queryClient.invalidateQueries({ queryKey: ["admin-women"] })
            }
          />
        )}
      </div>
      <footer>
        <button className="secondary" onClick={onClose}>
          取消
        </button>
        {woman && !woman.archived_at && (
          <button className="danger" onClick={() => archive.mutate()}>
            停用并归档
          </button>
        )}
        <button className="primary" disabled={!form.countryCode || !form.cityCode || save.isPending} onClick={() => save.mutate()}>
          <Save />
          保存
        </button>
      </footer>
    </aside>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
}) {
  return (
    <label>
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function MembersAdmin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const query = useQuery({
    queryKey: ["admin-members", debouncedSearch],
    queryFn: () => api<{ members: Member[]; total: number }>(`/admin/members?q=${encodeURIComponent(debouncedSearch)}`),
  });
  const vip = useMutation({
    mutationFn: ({ id, isVip }: { id: string; isVip: boolean }) =>
      api(
        `/admin/members/${id}/vip`,
        json("PATCH", { isVip, expiresAt: null }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-members"] }),
  });
  return (
    <section className="admin-table-card full">
      <div className="table-toolbar">
        <div>
          <h2>男会员</h2>
          <p>临时账户、正式账户与VIP状态</p>
        </div>
      </div>
      <label className="admin-search">
        <Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索姓名、邮箱或IP城市" />
        {search && <button onClick={() => setSearch("")}>清除</button>}
      </label>
      <table>
        <thead>
          <tr>
            <th>用户</th>
            <th>账户</th>
            <th>邮箱</th>
            <th>IP归属地</th>
            <th>创建时间</th>
            <th>VIP</th>
          </tr>
        </thead>
        <tbody>
          {query.data?.members.map((member) => (
            <tr key={member.id}>
              <td>
                <b>{member.name}</b>
                <small>{member.id.slice(0, 8)}</small>
              </td>
              <td>{member.account_status}</td>
              <td>{member.email ?? "—"}</td>
              <td>{member.city_zh ? `${member.country_zh ?? ""} · ${member.city_zh}` : "尚未识别"}</td>
              <td>{new Date(member.created_at).toLocaleString()}</td>
              <td>
                <button
                  className={`vip-toggle ${member.is_vip ? "active" : ""}`}
                  onClick={() =>
                    vip.mutate({ id: member.id, isVip: !member.is_vip })
                  }
                >
                  {member.is_vip ? "VIP 已开通" : "设为 VIP"}
                </button>
              </td>
            </tr>
          ))}
          {!query.isLoading && !query.isError && query.data?.members.length === 0 && <tr><td colSpan={6}>没有找到匹配的男会员</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

function ReadOnlyTable({
  endpoint,
  field,
}: {
  endpoint: string;
  field: string;
}) {
  const query = useQuery({
    queryKey: [endpoint],
    queryFn: () =>
      api<Record<string, Array<Record<string, unknown>>>>(endpoint),
  });
  const rows = query.data?.[field] ?? [];
  const columns = rows[0] ? Object.keys(rows[0]) : [];
  return (
    <section className="admin-table-card full">
      <div className="table-toolbar">
        <div>
          <h2>{field === "jobs" ? "自动招呼任务" : "会话只读视图"}</h2>
          <p>
            {field === "jobs"
              ? "可查看状态和失败原因，不在此处人工发送"
              : "管理员只能查看，不能冒充女性回复"}
          </p>
        </div>
        <Settings2 />
      </div>
      <div className="wide-table">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td key={column}>
                    {typeof row[column] === "object"
                      ? JSON.stringify(row[column])
                      : String(row[column] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
